import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useDesignStore } from '../../stores/designStore';
import { useChatStore } from '../../stores/chatStore';
import { useAsyncDesign } from '../../hooks/useAsyncDesign';
import type { DesignTask } from '../../services/api';

interface HistoryItemProps {
  item: {
    id: string;
    prompt: string;
    timestamp: number;
    applied: boolean;
  };
  onClick: () => void;
}

const HistoryItem: React.FC<HistoryItemProps> = ({ item, onClick }) => {
  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <button
      onClick={onClick}
      className="w-full text-left p-2 rounded-lg hover:bg-gray-50 transition-colors group"
    >
      <div className="flex items-start gap-2">
        <div className={`
          w-2 h-2 rounded-full mt-1.5 flex-shrink-0
          ${item.applied ? 'bg-green-400' : 'bg-gray-300'}
        `} />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-700 truncate group-hover:text-purple-600 transition-colors">
            {item.prompt}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">{formatTime(item.timestamp)}</p>
        </div>
      </div>
    </button>
  );
};

// 任务状态显示组件
const TaskStatusPanel: React.FC<{
  task: DesignTask;
  onCancel: () => void;
  getStatusText: (status: string) => string;
  getEstimatedTime: () => string | null;
}> = ({ task, onCancel, getStatusText, getEstimatedTime }) => {
  const isActive = task.status === 'pending' || task.status === 'processing';
  
  return (
    <div className="px-4 py-3 bg-gradient-to-r from-purple-50 to-blue-50 border-b border-purple-100">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isActive ? (
            <svg className="animate-spin h-4 w-4 text-purple-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : task.status === 'completed' ? (
            <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="h-4 w-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          <span className="text-sm font-medium text-gray-700">
            {getStatusText(task.status)}
          </span>
        </div>
        {isActive && (
          <button
            onClick={onCancel}
            className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 transition-colors"
          >
            取消
          </button>
        )}
      </div>
      {isActive && getEstimatedTime() && (
        <p className="text-xs text-gray-500 mt-1 ml-6">{getEstimatedTime()}</p>
      )}
      {task.processingTime && task.status === 'completed' && (
        <p className="text-xs text-green-600 mt-1 ml-6">
          耗时 {(task.processingTime / 1000).toFixed(1)} 秒
        </p>
      )}
    </div>
  );
};

// 确认对话框组件
const ConfirmDialog: React.FC<{
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ isOpen, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900">确认重新生成</h3>
        </div>
        <p className="text-gray-600 mb-6">
          是否需要重新将业务AI回答内容生成可视化页面？
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            取消
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:shadow-md transition-all"
          >
            确认
          </button>
        </div>
      </div>
    </div>
  );
};

export const VisualAIInput: React.FC = () => {
  const {
    visualAIHistory,
    addVisualAIHistory,
    themeConfig,
    previewHtml,
  } = useDesignStore();

  const [inputValue, setInputValue] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [hasGeneratedFromAI, setHasGeneratedFromAI] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // AI联动：获取业务AI的最后回复（不检查时间过期）
  const lastAIResponse = useChatStore((state) => state.lastAIResponse);
  
  // 调试：监听 lastAIResponse 变化
  useEffect(() => {
    console.log('[VisualAIInput] lastAIResponse changed:', lastAIResponse ? '有内容' : '无内容', lastAIResponse?.slice(0, 50));
  }, [lastAIResponse]);
  
  // 检查 localStorage 中的数据（调试用）
  useEffect(() => {
    const storage = localStorage.getItem('chat-storage');
    console.log('[VisualAIInput] localStorage chat-storage:', storage ? '存在' : '不存在');
    if (storage) {
      const data = JSON.parse(storage);
      console.log('[VisualAIInput] lastAIResponse in storage:', data.state?.lastAIResponse ? '有内容' : '无内容');
    }
  }, []);

  // 使用异步设计hook
  const {
    currentTask,
    isGenerating,
    submitDesignTask,
    cancelTask,
    getStatusText,
    getEstimatedTime,
  } = useAsyncDesign({
    onSuccess: () => {
      // 任务成功完成
      addVisualAIHistory({
        prompt: inputValue.trim() || '基于业务AI建议生成',
        applied: true,
      });
      setInputValue('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    },
    onError: (errorMsg) => {
      setError(errorMsg);
    },
  });

  // 处理输入变化
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    setError(null);
  };

  // 调整textarea高度
  const adjustTextareaHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, []);

  // 处理键盘事件
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // 使用历史指令
  const applyHistoryItem = (prompt: string) => {
    setInputValue(prompt);
    setShowHistory(false);
    setError(null);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  // 提交设计指令（带指定prompt）
  const handleSubmitWithPrompt = async (prompt: string) => {
    if (!prompt.trim() || isGenerating) return;

    setError(null);

    try {
      // 提交异步任务
      await submitDesignTask({
        prompt,
        themeConfig: {
          primaryColor: themeConfig.primaryColor,
          secondaryColor: themeConfig.secondaryColor,
          backgroundColor: themeConfig.backgroundColor,
          textColor: themeConfig.textColor,
          fontFamily: themeConfig.fontFamily,
          borderRadius: themeConfig.borderRadius,
        },
        currentHtml: previewHtml || undefined,
        model: 'kimi-k2.5',
      });
    } catch (err: any) {
      // 错误已在hook中处理
    }
  };

  // 提交设计指令
  const handleSubmit = async () => {
    let prompt = inputValue.trim();
    if (!prompt || isGenerating) return;

    // AI联动：检测用户是否想生成页面但缺乏具体上下文
    const vagueKeywords = ['生成可视化页面', '生成页面', '创建页面', '制作页面', '设计页面'];
    const isVaguePrompt = vagueKeywords.some(keyword => prompt.includes(keyword));

    if (isVaguePrompt) {
      if (lastAIResponse) {
        // 有业务AI上下文，自动融合
        prompt = `基于以下业务建议生成页面：\n\n${lastAIResponse}\n\n用户指令：${prompt}\n\n请生成一个完整的网页设计。`;
      } else {
        // 没有业务AI上下文，提示用户补充输入
        setError('请补充描述您想要生成的页面内容，例如：\n"生成一个包含标题、图片和按钮的登录页面"\n或者先在左侧"业务AI助手"中描述您的需求。');
        return;
      }
    } else if (prompt.length < 10) {
      // 输入内容太短，提示补充
      setError('描述内容太短，请补充更多细节，例如页面功能、布局、样式等。');
      return;
    }

    await handleSubmitWithPrompt(prompt);
  };

  // 处理取消
  const handleCancel = () => {
    cancelTask();
  };

  // 执行业务AI可视化生成
  const doBusinessAIGenerate = () => {
    if (!lastAIResponse) return;
    
    // 构建增强的prompt并直接提交
    const enhancedPrompt = `基于以下业务建议生成页面：\n\n${lastAIResponse}\n\n请生成一个完整的网页设计。`;
    handleSubmitWithPrompt(enhancedPrompt);
    setHasGeneratedFromAI(true);
  };

  // 处理业务AI可视化生成按钮点击
  const handleBusinessAIGenerate = () => {
    // 检查是否有业务AI回答内容
    console.log('[handleBusinessAIGenerate] lastAIResponse:', lastAIResponse ? '有内容' : '无内容');
    if (!lastAIResponse) {
      setError('无业务AI回答内容（请先在左侧业务AI助手提问）');
      return;
    }
    
    // 第一次点击直接生成，之后点击显示确认对话框
    if (!hasGeneratedFromAI) {
      doBusinessAIGenerate();
    } else {
      setShowConfirmDialog(true);
    }
  };

  // 确认重新生成
  const handleConfirmRegenerate = () => {
    setShowConfirmDialog(false);
    doBusinessAIGenerate();
  };

  // 取消重新生成
  const handleCancelRegenerate = () => {
    setShowConfirmDialog(false);
  };

  // 快速指令
  const quickCommands = [
    { icon: '🎨', label: '更换主题色', prompt: '将主题色改为蓝色' },
    { icon: '📐', label: '调整布局', prompt: '将布局改为两列' },
    { icon: '✨', label: '添加动画', prompt: '添加淡入动画效果' },
    { icon: '📝', label: '修改文字', prompt: '将标题改为更大字号' },
    { icon: '🤖', label: '业务AI可视化生成', action: handleBusinessAIGenerate, isDynamic: true },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* 确认对话框 */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onConfirm={handleConfirmRegenerate}
        onCancel={handleCancelRegenerate}
      />

      {/* 头部：标题和历史按钮 */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-gradient-to-br from-pink-500 to-orange-500 rounded-full flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
          </div>
          <span className="text-sm font-medium text-gray-700">可视化AI助手</span>
        </div>
        <button
          onClick={() => setShowHistory(!showHistory)}
          className={`
            text-xs px-2 py-1 rounded transition-colors
            ${showHistory ? 'bg-purple-100 text-purple-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'}
          `}
        >
          历史 ({visualAIHistory.length})
        </button>
      </div>

      {/* 任务状态面板 */}
      {currentTask && (
        <TaskStatusPanel
          task={currentTask}
          onCancel={handleCancel}
          getStatusText={getStatusText}
          getEstimatedTime={getEstimatedTime}
        />
      )}

      {/* 历史记录面板 */}
      {showHistory && visualAIHistory.length > 0 && (
        <div className="max-h-32 overflow-y-auto border-b bg-white">
          <div className="p-2 space-y-1">
            {visualAIHistory.slice(0, 10).map((item) => (
              <HistoryItem
                key={item.id}
                item={item}
                onClick={() => applyHistoryItem(item.prompt)}
              />
            ))}
          </div>
        </div>
      )}

      {/* 快速指令 */}
      <div className="px-4 py-2 border-b bg-white">
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {quickCommands.map((cmd, index) => (
            <button
              key={index}
              onClick={() => cmd.isDynamic ? cmd.action?.() : applyHistoryItem(cmd.prompt!)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${
                cmd.isDynamic 
                  ? 'bg-gradient-to-r from-amber-100 to-orange-100 hover:from-amber-200 hover:to-orange-200 text-amber-700 border border-amber-200' 
                  : 'bg-gray-100 hover:bg-purple-50 text-gray-600 hover:text-purple-600'
              }`}
            >
              <span>{cmd.icon}</span>
              <span>{cmd.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-100">
          <p className="text-xs text-red-600 flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </p>
        </div>
      )}

      {/* 输入区 */}
      <div className="flex-1 p-3">
        <div className="flex gap-2 h-full">
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onInput={adjustTextareaHeight}
              placeholder={isGenerating 
                ? "AI正在设计中，请稍候..." 
                : "输入设计指令，如：设计一个带有编辑框和确认按钮的居中布局页面..."
              }
              className={`w-full h-full min-h-[60px] max-h-[100px] px-3 py-2 text-sm border rounded-lg resize-none focus:outline-none focus:ring-2 focus:border-transparent transition-colors ${
                error 
                  ? 'border-red-300 focus:ring-red-200' 
                  : 'border-gray-200 focus:ring-purple-500 focus:border-transparent'
              }`}
              disabled={isGenerating}
            />
            <div className="absolute bottom-2 right-2 text-xs text-gray-400">
              Enter 发送 | Shift+Enter 换行
            </div>
          </div>
          <button
            onClick={handleSubmit}
            disabled={!inputValue.trim() || isGenerating}
            className={`
              px-4 py-2 rounded-lg font-medium text-sm transition-all flex-shrink-0
              ${!inputValue.trim() || isGenerating
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:shadow-lg'
              }
            `}
          >
            {isGenerating ? (
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
